// src/index.js
import "./firebase-app-core";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "bootstrap/dist/css/bootstrap.min.css";
import "./iosNoFocusZoom";
// PWA "Add to Home Screen": importing this attaches the beforeinstallprompt
// listener at boot (so the event isn't missed); registerPwaServiceWorker()
// flips the site to "installable" for Android's native install dialog.
import { registerPwaServiceWorker } from "./pwa/installPrompt";

// 2026-05-07: deferred Firestore IndexedDB cache wipe.
// AuthContext.logout() can no longer call terminate(db) inline (doing
// so kills the module-level singleton and breaks the next signup /
// login). Instead, logout sets `lex:firestore-wipe-on-next-boot` in
// localStorage, and we honour it on the very next page load — BEFORE
// React renders, BEFORE any component opens an onSnapshot — by
// directly deleting the IndexedDB databases Firestore uses. We avoid
// `terminate(db)` entirely because (a) we don't even need it (no one
// has used `db` yet on this fresh boot), and (b) `terminate()` would
// permanently kill the singleton this app keeps importing.
const FIRESTORE_WIPE_FLAG_KEY = "lex:firestore-wipe-on-next-boot";
async function wipeFirestoreIndexedDb() {
  if (typeof indexedDB === "undefined") return;
  // Firestore uses two IDB databases: "firestore/[default]/<projectId>/main"
  // (modular SDK) and a couple of legacy variants. Cheapest reliable wipe
  // is to enumerate and delete anything matching the firestore prefix.
  let dbs = [];
  try {
    if (typeof indexedDB.databases === "function") {
      dbs = (await indexedDB.databases()) || [];
    }
  } catch {}
  const names = new Set();
  dbs.forEach((entry) => {
    const n = entry?.name || "";
    if (n.startsWith("firestore/")) names.add(n);
  });
  // Also blow away the well-known fallback names in case databases() isn't
  // implemented (Safari < 14 / Firefox quirks).
  // The exact name is `firestore/[default]/<projectId>/main`. We don't
  // know the projectId here, but deleting a non-existent IDB is a no-op.
  await Promise.all(
    Array.from(names).map(
      (name) =>
        new Promise((resolve) => {
          const req = indexedDB.deleteDatabase(name);
          req.onsuccess = () => resolve();
          req.onerror = () => resolve();
          req.onblocked = () => resolve();
        })
    )
  );
}
let pendingFirestoreWipe = null;
if (typeof window !== "undefined") {
  try {
    if (window.localStorage.getItem(FIRESTORE_WIPE_FLAG_KEY) === "1") {
      window.localStorage.removeItem(FIRESTORE_WIPE_FLAG_KEY);
      pendingFirestoreWipe = wipeFirestoreIndexedDb().catch((e) => {
        console.warn("[boot] deferred Firestore wipe failed:", e);
      });
    }
  } catch {}
}

// 2026-05-11: defer GA/gtag boot so analytics does not compete with
// first paint on slow devices/connections. We still keep dataLayer/gtag
// shims ready immediately so early `track(...)` calls are buffered.
const GA_MEASUREMENT_ID =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_GA_MEASUREMENT_ID) ||
  "G-DCY1HB8W4K";

function bootDeferredAnalytics() {
  if (typeof window === "undefined") return;
  if (process.env.NODE_ENV !== "production") return;
  if (!GA_MEASUREMENT_ID) return;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }

  const load = () => {
    if (window.__LEX_GTAG_BOOTSTRAPPED__) return;
    window.__LEX_GTAG_BOOTSTRAPPED__ = true;

    const script = document.createElement("script");
    script.async = true;
    script.src =
      "https://www.googletagmanager.com/gtag/js?id=" +
      encodeURIComponent(GA_MEASUREMENT_ID);
    script.onload = () => {
      try {
        window.gtag("js", new Date());
        window.gtag("config", GA_MEASUREMENT_ID);
      } catch {}
    };
    script.onerror = () => {
      window.__LEX_GTAG_BOOTSTRAPPED__ = false;
    };
    document.head.appendChild(script);
  };

  const schedule = () => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(load, { timeout: 4000 });
    } else {
      setTimeout(load, 2200);
    }
  };

  if (document.readyState === "complete") {
    schedule();
  } else {
    window.addEventListener("load", schedule, { once: true });
  }
}

/*
 * Production console silencing.
 *
 * The codebase has 145+ console.log / console.warn / console.info calls
 * scattered across components. In dev they're useful; in production they
 * cost real CPU on mobile (object stringification, devtools queue, etc)
 * AND leak debug info to end users. Silence the noisy levels in prod
 * while keeping console.error so real failures still surface in
 * monitoring tools.
 *
 * Note on ordering: ES module `import` statements are hoisted by the
 * spec, so imported modules run before this block regardless of where
 * we put the patch. This is fine because our own modules don't log at
 * module-init time — the noisy logs all fire from runtime code (event
 * handlers, effects, renders) which runs AFTER this block.
 */
if (typeof window !== "undefined" && process.env.NODE_ENV === "production") {
  const noop = () => {};
  ["log", "info", "debug", "warn"].forEach((m) => {
    if (typeof console[m] === "function") console[m] = noop;
  });
}

function isResizeObserverLoopMessage(input) {
  const msg = String(input || "");
  return (
    msg.includes("ResizeObserver loop completed with undelivered notifications") ||
    msg.includes("ResizeObserver loop limit exceeded")
  );
}

function hideResizeObserverOverlay() {
  const candidates = [
    "#webpack-dev-server-client-overlay",
    "#webpack-dev-server-client-overlay-div",
    "iframe#webpack-dev-server-client-overlay",
    "iframe[src*='react-error-overlay']",
    "iframe[src*='webpackHotDevClient']",
  ];
  candidates.forEach((selector) => {
    const nodes = document.querySelectorAll(selector);
    nodes.forEach((node) => {
      try {
        node.style.display = "none";
        node.setAttribute("aria-hidden", "true");
      } catch {}
    });
  });
}

// Guard against noisy ResizeObserver loop warnings in dev overlay.
if (typeof window !== "undefined") {
  if (typeof window.ResizeObserver === "function" && !window.__LEX_SAFE_RESIZE_OBSERVER__) {
    const NativeResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class SafeResizeObserver extends NativeResizeObserver {
      constructor(callback) {
        super((entries, observer) => {
          window.requestAnimationFrame(() => callback(entries, observer));
        });
      }
    };
    window.__LEX_SAFE_RESIZE_OBSERVER__ = true;
  }

  if (!window.__LEX_RESIZE_NOISE_FILTER__) {
    const swallowResizeNoise = (event) => {
      const msg = String(event?.message || event?.error?.message || "");
      if (!isResizeObserverLoopMessage(msg)) return;
      event.preventDefault?.();
      event.stopImmediatePropagation?.();
      hideResizeObserverOverlay();
    };
    const swallowResizeNoiseRejection = (event) => {
      const reason = event?.reason;
      const msg = String(reason?.message || reason || "");
      if (!isResizeObserverLoopMessage(msg)) return;
      event.preventDefault?.();
      event.stopImmediatePropagation?.();
      hideResizeObserverOverlay();
    };

    window.addEventListener("error", swallowResizeNoise, true);
    window.addEventListener("unhandledrejection", swallowResizeNoiseRejection, true);
    window.__LEX_RESIZE_NOISE_FILTER__ = true;
  }
}

// ✅ 关键：把构建时 env 注入到 window，给 IIFE 脚本用
window.__MAPBOX_TOKEN__ = process.env.REACT_APP_MAPBOX_TOKEN || "";

// Get the root element
const rootElement = document.getElementById("root");

function mountApp() {
  if (!rootElement) {
    console.error("Root container is missing in index.html.");
    return;
  }
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

// 2026-05-11: never hold first paint hostage for cache cleanup.
// If a wipe was requested, run it in the background.
mountApp();

if (pendingFirestoreWipe) {
  pendingFirestoreWipe.finally(() => {
    try {
      window.__LEX_FIRESTORE_WIPE_DONE__ = true;
    } catch {}
  });
}

bootDeferredAnalytics();

// Register the minimal PWA service worker so Android/Chrome treats the site
// as installable (prerequisite for the "Add to Home Screen" badge).
registerPwaServiceWorker();

reportWebVitals();
